import {useSafeChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {referenceID} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {useQuery} from '@github-ui/react-query'
import {useEffect} from 'react'

import type {File} from '../components/ContentPreview/content-preview-types'
import type {ContentPreviewContext} from '../components/ContentPreview/ContentPreviewContext'

/**
 * If the given file comes from a reference and has no content, will attempt to retrieve that content.
 *
 * Hydration needs the chat service, so on hosts with no chat provider this is a no-op. Files that
 * already carry their contents (artifacts, for example) render without it.
 */
export function useHydrateReference(file: File, updateItem: ContentPreviewContext['updateItem']) {
  const {reference} = file
  const manager = useSafeChatManager()
  // eslint-disable-next-line @tanstack/query/exhaustive-deps, @github-ui/github-monorepo/require-query-persister -- reference data is immutable (keyed by OID), no need to persist
  const {isLoading, isError, data} = useQuery({
    queryKey: [
      'copilot-reference-preview',
      'copilot',
      'references',
      reference?.type,
      reference ? referenceID(reference) : undefined,
    ],
    queryFn: async () => {
      if (!manager || !reference || reference.type !== 'file' || !!file.value) return null

      const result = await manager.service.hydrateReference(reference)
      if (!result.ok) return null

      updateItem({...file, value: result.payload.contents, isStreaming: false})

      return result.payload
    },
    // references include an OID and are thus not going to change ever
    staleTime: Number.POSITIVE_INFINITY,
    // Without a chat manager there is nothing to hydrate from. Skip the query rather than caching a
    // null result, which staleTime: Infinity would keep fresh and hide a later chat-hosted fetch.
    enabled: !!manager,
  })

  useEffect(() => {
    // if the query is cached we might still need to run updateItem
    if (data?.contents && file.value !== data.contents) {
      updateItem({...file, value: data.contents, isStreaming: false})
    }
  }, [data?.contents, file, updateItem])

  return {isLoading, isError, data}
}
